import {
  addWeeks,
  differenceInCalendarDays,
  endOfWeek,
  format,
  isWithinInterval,
  isTomorrow,
  startOfDay,
  startOfWeek,
} from "date-fns";
import { User } from "features/User/models";
import { isInPast } from "helpers/time";
import { sortDateTime } from "helpers/sort";
import { startCase } from "lodash";
import { UserRoleOrEmailOption } from "components/FormElements/Fields/SelectFields/UserRoleOrEmailMultiSelect";
import { userRoleEmailId } from "helpers/string";
import { ActionSummary } from "features/Resources/Shared/models";
import { LeadPerson, Meeting, MeetingAttendee, MeetingStatus } from "./models";
import { INACTIVE_MEETING_STATUSES } from "./constants";

export function isLead(meeting: Meeting, user: User) {
  return user?.id === meeting?.leadId;
}

export function isAttendee(meeting: Meeting, user: User) {
  if (!meeting?.attendeeIds) {
    return false;
  }
  return meeting.attendeeIds.includes(user?.id);
}

export function meetingScheduledDate(action: Meeting) {
  return action?.scheduledAt ?? action?.proposedAt;
}

export function isMeetingInPast(task: Meeting) {
  return isInPast(task?.scheduledAt);
}

export function isMeetingInFuture(task: Meeting) {
  return !isInPast(task.scheduledAt);
}

export function filterMeetingsByText(meetings: Meeting[] | undefined, searchText: string | "") {
  if (!meetings) return [];
  if ((searchText || "").length < 3) {
    return meetings;
  }

  const text = searchText.toLowerCase();
  return meetings.filter(
    (meeting) =>
      meeting?.name?.toLowerCase()?.includes(text) ||
      meeting?.status?.toLowerCase()?.includes(text) ||
      meeting?.additionalInfo?.toLowerCase().includes(text) ||
      meeting?.purpose?.toLowerCase().includes(text) ||
      meeting?.learningObjective?.toLowerCase().includes(text),
  );
}

export function sortMeetings(meetings: Array<Meeting>, direction: "asc" | "desc" = "asc"): Array<Meeting> {
  return sortDateTime(meetings, (action) => meetingScheduledDate(action), direction);
}

export function attendeeToUserRoleOrEmailId(meetingAttendee: MeetingAttendee) {
  return userRoleEmailId(meetingAttendee?.emailAddress, "User", meetingAttendee?.userId, meetingAttendee?.emailType);
}

export function toUserRoleOrEmailOption(meetingAttendee: MeetingAttendee): UserRoleOrEmailOption {
  const recordId = meetingAttendee?.userId;
  let label = meetingAttendee?.emailAddress;
  let name = meetingAttendee?.emailAddress;
  if (meetingAttendee?.user) {
    name = meetingAttendee?.user?.fullName;
    if (meetingAttendee?.emailType === "personal") {
      label = `${startCase(name)} (${meetingAttendee?.emailType})`;
    } else {
      label = startCase(name);
    }
  }
  return {
    recordType: meetingAttendee?.emailAddress ? "CustomEmail" : "User",
    recordId,
    id: attendeeToUserRoleOrEmailId(meetingAttendee),
    label: label,
    email: meetingAttendee?.emailAddress,
    name: name,
    emailType: meetingAttendee?.emailType,
  };
}

export function fromUserRoleOrEmailOption(userRoleOrEmailOption: UserRoleOrEmailOption) {
  const user = {} as Partial<User>;
  if (userRoleOrEmailOption.recordType === "User") {
    user.fullName = userRoleOrEmailOption?.name;
  }
  const base = {
    userId: userRoleOrEmailOption?.email ? undefined : userRoleOrEmailOption?.recordId,
    emailAddress: userRoleOrEmailOption?.email,
    emailType: userRoleOrEmailOption?.emailType,
  };
  if (Object.keys(user).length > 0) {
    return {
      ...base,
      user,
    };
  }
  return base;
}

export function meetingTimingDescriptor(meeting: Meeting) {
  return "";
}

/** Group an array of meetings into date buckets, sorted chronologically. */
export function groupMeetingsByDate(meetings: Meeting[]): Array<{ key: string; date: Date; meetings: Meeting[] }> {
  const map = new Map<string, { date: Date; meetings: Meeting[] }>();
  for (const m of meetings) {
    const d = new Date(m.startTime);
    const key = format(d, "yyyy-MM-dd");
    if (!map.has(key)) map.set(key, { date: d, meetings: [] });
    map.get(key)!.meetings.push(m);
  }
  return Array.from(map.entries()).map(([key, { date, meetings: ms }]) => ({ key, date, meetings: ms }));
}

/** Human-readable date label for upcoming schedule groups ("Tomorrow · Mon, Jun 5", "Next week · …"). */
export function upcomingDateLabel(date: Date): string {
  if (isTomorrow(date)) return `Tomorrow · ${format(date, "EEE, MMM d")}`;
  const now = new Date();
  const nextWeekStart = addWeeks(startOfWeek(now, { weekStartsOn: 1 }), 1);
  const nextWeekEnd = endOfWeek(nextWeekStart, { weekStartsOn: 1 });
  if (isWithinInterval(startOfDay(date), { start: nextWeekStart, end: nextWeekEnd })) {
    return `Next week · ${format(date, "EEE, MMM d")}`;
  }
  return format(date, "EEE, MMM d");
}

/** Returns a short label for the other attendees visible on a meeting row ("Alice & Bob", "Alice +2 others"). */
export function getAttendeeDisplayLabel(meeting: Meeting, currentUserId?: string): string | null {
  const names = (meeting.attendees || [])
    .filter((a) => a.userId !== currentUserId)
    .map((a) => a.user?.fullName)
    .filter((n): n is string => !!n);
  if (names.length === 0) return null;
  if (names.length <= 2) return names.join(" & ");
  return `${names[0]} +${names.length - 1} others`;
}

/**
 * Meetings the system tried to place but couldn't (no time on the calendar).
 * `failed` = an automated scheduling attempt that raised (see Meeting::Runner); it needs the
 * same remedy as `pending_scheduling` — put it on the calendar — so they share a bucket.
 */
export const NEEDS_SCHEDULING_STATUSES: MeetingStatus[] = ["pending_scheduling", "failed"];

/** Active statuses that belong to a specific day in the by-day sections. */
const DAY_SCHEDULED_STATUSES: MeetingStatus[] = ["scheduled", "completed", "cancelled"];

/**
 * Calendar-day offset of `date` from the journey `startDate`. Start date = day 1, weekends counted.
 * Mirrors BulkScheduleContent.groupMeetingsByDay so day numbers stay consistent across the app.
 * Parses bare `YYYY-MM-DD` strings as local midnight to avoid a UTC off-by-one.
 */
export function dayOffsetFromStart(date: Date | string, startDate: Date | string): number {
  return differenceInCalendarDays(toLocalDate(date), toLocalDate(startDate)) + 1;
}

function toLocalDate(value: Date | string): Date {
  if (typeof value === "string") {
    const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  return new Date(value);
}

export interface MeetingDayBucket {
  key: string; // yyyy-MM-dd
  date: Date;
  scheduled: Meeting[];
  skipped: Meeting[];
}

export interface MeetingBuckets {
  needsScheduling: Meeting[];
  days: MeetingDayBucket[];
}

/**
 * Split meetings into the three zones the schedule view renders:
 * - `needsScheduling` — pending/failed, sorted by target date
 * - `days` — scheduled/completed/cancelled and skipped, grouped by their date and sorted
 * `removed` meetings are dropped.
 */
export function bucketMeetings(meetings: Meeting[] | undefined): MeetingBuckets {
  const needsScheduling: Meeting[] = [];
  const dayMap = new Map<string, MeetingDayBucket>();

  const dayBucketFor = (meeting: Meeting): MeetingDayBucket | null => {
    // Skipping clears scheduledAt, so skipped meetings fall back to proposedAt; that can be null
    // (the backend treats proposed_at as nullable), so use statusUpdatedAt as a last resort rather
    // than dropping the meeting from the schedule entirely.
    const dateValue = meetingScheduledDate(meeting) ?? meeting.statusUpdatedAt;
    if (!dateValue) return null;
    const date = new Date(dateValue);
    const key = format(date, "yyyy-MM-dd");
    let bucket = dayMap.get(key);
    if (!bucket) {
      bucket = { key, date, scheduled: [], skipped: [] };
      dayMap.set(key, bucket);
    }
    return bucket;
  };

  for (const meeting of meetings ?? []) {
    if (meeting.status === "removed") continue;
    if (NEEDS_SCHEDULING_STATUSES.includes(meeting.status)) {
      needsScheduling.push(meeting);
    } else if (meeting.status === "skipped") {
      dayBucketFor(meeting)?.skipped.push(meeting);
    } else if (DAY_SCHEDULED_STATUSES.includes(meeting.status)) {
      dayBucketFor(meeting)?.scheduled.push(meeting);
    }
  }

  needsScheduling.sort((a, b) => meetingTime(a) - meetingTime(b));

  const days = Array.from(dayMap.values());
  for (const day of days) {
    day.scheduled.sort((a, b) => meetingTime(a) - meetingTime(b));
    day.skipped.sort((a, b) => meetingTime(a) - meetingTime(b));
  }
  days.sort((a, b) => a.date.getTime() - b.date.getTime());

  return { needsScheduling, days };
}

function meetingTime(meeting: Meeting): number {
  const dateValue = meeting.scheduledAt ?? meeting.proposedAt ?? meeting.startTime;
  return dateValue ? new Date(dateValue).getTime() : Infinity;
}

export type ReadinessLevel = "clear" | "needs_scheduling" | "attention" | "neutral";

export interface MeetingReadiness {
  level: ReadinessLevel;
  /** Callout label. Empty for `clear` — a clean meeting gets no pill (callouts only). */
  label: string;
}

/**
 * Per-meeting callouts for the schedule view. We surface problems only (declined,
 * needs-scheduling; not-on-invite/issues layered on later) — never a "ready to go"
 * claim. A clean meeting (incl. pending responses) is `clear` with no pill.
 */
/** The session lead as a normalized LeadPerson, derived from the flagged attendee. */
export function leadFromAttendees(attendees: MeetingAttendee[] | undefined): LeadPerson | undefined {
  const lead = attendees?.find((attendee) => attendee.isLead)?.user;
  return lead ? { id: String(lead.id), name: lead.fullName, avatarUrl: lead.avatarUrl } : undefined;
}

export function meetingReadiness(meeting: Meeting): MeetingReadiness {
  if (NEEDS_SCHEDULING_STATUSES.includes(meeting.status)) {
    return { level: "needs_scheduling", label: "Needs scheduling" };
  }
  // Only an upcoming (scheduled) meeting can need attention — a completed/cancelled/skipped one
  // is done, so a decline on it is history, not a "Reschedule" call to action.
  if (meeting.status !== "scheduled") {
    return { level: meeting.status === "skipped" ? "neutral" : "clear", label: "" };
  }

  const attendees = meeting.attendees ?? [];
  const lead = attendees.find((a) => a.isLead);
  if (lead?.responseStatus === "declined") {
    return { level: "attention", label: "Lead declined" };
  }

  const declined = attendees.filter(
    (a) => !a.isLead && a.invitationType === "required" && a.responseStatus === "declined",
  );
  if (declined.length > 0) {
    return { level: "attention", label: declined.length === 1 ? "1 declined" : `${declined.length} declined` };
  }

  return { level: "clear", label: "" };
}

/** Get upcoming meetings sorted by date, excluding inactive statuses. */
export function getUpcomingMeetings(actions: ActionSummary[] | undefined, limit?: number): ActionSummary[] {
  if (!actions) return [];

  const sorted = actions
    .filter((a) => a.actionType === "meeting")
    .filter((a) => !INACTIVE_MEETING_STATUSES.includes(a.action.status))
    .sort((a, b) => {
      const dateA = a.unifiedDate ? new Date(a.unifiedDate).getTime() : Infinity;
      const dateB = b.unifiedDate ? new Date(b.unifiedDate).getTime() : Infinity;
      return dateA - dateB;
    });

  return limit ? sorted.slice(0, limit) : sorted;
}
